/**
 * RTL for voice / transcript text when the widget is in a right-to-left locale.
 * Uses explicit `direction: 'rtl'` from merged config, or language base `he` / `ar`
 * when direction was not set (or was lost on nested config spreads).
 *
 * @param {{ direction?: string, language?: string } | null | undefined} config
 * @returns {boolean}
 */
export function configPrefersRtlText(config) {
  if (!config || typeof config !== 'object') return false;
  if (config.direction === 'rtl') return true;
  const base = String(config.language || '').toLowerCase().split(/[-_]/)[0];
  return base === 'he' || base === 'ar';
}

/**
 * `dir` for elements showing streamed assistant / agent text (and similar dynamic strings).
 * Uses `rtl` when the widget is explicitly RTL; otherwise `auto` so Hebrew (or other RTL)
 * agent copy still lays out correctly when Language/Direction are English + LTR.
 *
 * @param {{ direction?: string, language?: string } | null | undefined} config
 * @returns {'rtl' | 'auto'}
 */
export function dirForAssistantStreamingText(config) {
  return configPrefersRtlText(config) ? 'rtl' : 'auto';
}

const RE_HEBREW = /[\u0590-\u05FF]/;
const RE_ARABIC = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * `dir` for a single assistant / agent transcript string.
 * When the widget chrome is LTR but the line contains Hebrew or Arabic letters,
 * use explicit `rtl` so neutral punctuation (e.g. "?") stays at the logical sentence end.
 *
 * @param {string|null|undefined} text
 * @param {{ direction?: string, language?: string } | null | undefined} config
 * @returns {'rtl' | 'ltr' | 'auto'}
 */
export function dirForTranscriptSnippet(text, config) {
  if (configPrefersRtlText(config)) return 'rtl';
  const s = text != null ? String(text) : '';
  if (!s.trim()) return 'auto';
  if (RE_HEBREW.test(s) || RE_ARABIC.test(s)) return 'rtl';
  return 'ltr';
}
